import { MergeAnalysisService } from '../merge-bots/merge-analysis-service';
import { findLinkedBotInfo } from '@/services/shadow-bot-utils';
import { supabase } from '@/integrations/supabase/client';
import { normalizeInstrument, createInstrumentMapping, getChildBotIds } from '@/utils/instrument-utils';
import { createChildSettingsMap } from '@/utils/child-settings-utils';

export interface MergedTradeHistoryItem {
  timestamp: string;
  instrument: string;
  amount: number;
  action: string;
  normalized_pnl: number;
}

export interface TradingHistoryWithSource {
  trades: MergedTradeHistoryItem[];
  source: {
    parentBotId: string;
    parentBotName: string;
    parentBotType: 'original' | 'shadow';
    isFromLinkedBot: boolean;
  } | null;
}

export class PropBotTradeHistoryService {
  /**
   * Fetch realtime EXIT events from bot_signal_events for allowed bots
   */
  private static async fetchRealtimeExitEvents(
    allowedBotIds: string[], 
    instrumentMap: Map<string, { childBotId: string; childBotName?: string }>,
    childSettingsMap: Map<string, { tp_usd: number; sl_usd: number; quantity: number }>
  ): Promise<MergedTradeHistoryItem[]> {
    try {
      if (allowedBotIds.length === 0) {
        return [];
      }

      console.log('Fetching realtime EXIT events for allowed bots:', allowedBotIds);
      
      const { data: exitEvents, error } = await supabase
        .from('bot_signal_events')
        .select('*')
        .in('bot_id', allowedBotIds)
        .in('action', ['EXIT_LONG', 'EXIT_SHORT'])
        .order('timestamp', { ascending: false });

      if (error) {
        console.error('Error fetching realtime exit events:', error);
        return [];
      }

      console.log('Found realtime exit events:', exitEvents?.length || 0);

      // Convert exit events to MergedTradeHistoryItem format
      const realtimeItems: MergedTradeHistoryItem[] = (exitEvents || [])
        .map(event => {
          const normalizedInstrument = normalizeInstrument(event.instrument);
          
          // Check if this instrument belongs to our merged analysis
          // RELAX filter for parent bot (not in child list) - allow all instruments
          if (instrumentMap.size > 0) {
            const isChildBot = allowedBotIds.slice(0, -2).includes(event.bot_id); // Exclude parent + linked bot
            
            if (isChildBot) {
              // For child bots: strict instrument filter
              const fallbackInstrument = normalizedInstrument.replace(/USD/g, '');
              const hasMapping = instrumentMap.has(normalizedInstrument) || 
                                (fallbackInstrument && instrumentMap.has(fallbackInstrument));
              
              if (!hasMapping) {
                console.log('Skipping child bot event with unmapped instrument:', event.instrument, 'Available instruments:', Array.from(instrumentMap.keys()));
                return null;
              }
            } else {
              // For parent bot: accept all instruments (e.g., XAUUSD)
              console.log('Accepting parent bot event for instrument:', event.instrument);
            }
          }

          // Calculate normalized P&L using individual child bot settings
          // Handle different win field formats (0, 1, '1', true, false)
          const winValue = event.win;
          const isWin = winValue === true || (typeof winValue === 'number' && winValue === 1) || (typeof winValue === 'string' && winValue === '1');
          
          // Determine childBotId with priority system
          const mappingInfo = instrumentMap.get(normalizedInstrument) || instrumentMap.get(normalizedInstrument.replace(/USD/g, ''));
          const childBotId = 
            // Priority 1: Use child_bot_id from event (populated by backfill or edge function)
            event.child_bot_id || 
            // Priority 2: Use instrumentMap (from bot_merge_mapping or published config)
            mappingInfo?.childBotId || 
            // Priority 3: Fallback to parent bot id
            event.bot_id;
          
          const childSettings = childSettingsMap.get(childBotId);
          
          // Add logging for debugging
          if (!event.child_bot_id && !mappingInfo?.childBotId) {
            const payloadId = event.raw_payload && typeof event.raw_payload === 'object' && 'id' in event.raw_payload 
              ? event.raw_payload.id 
              : null;
            console.warn(`⚠️ No child_bot_id found for signal:`, {
              event_id: event.id,
              instrument: event.instrument,
              raw_payload_id: payloadId,
              fallback_to: event.bot_id
            });
          }
          
          console.log(`P&L calculation for ${event.instrument} (${normalizedInstrument}):`, {
            childBotId,
            hasChildSettings: !!childSettings,
            childSettings,
            amount: event.amount,
            isWin
          });
          
          // Calculate normalized P&L using TP/SL and quantity
          let normalized_pnl = 0;
          if (childSettings && event.amount && childSettings.quantity > 0) {
            const coefficient = event.amount / childSettings.quantity;
            normalized_pnl = isWin 
              ? coefficient * childSettings.tp_usd
              : -coefficient * childSettings.sl_usd;
            
            console.log(`Using child settings calculation:`, {
              coefficient,
              tp_usd: childSettings.tp_usd,
              sl_usd: childSettings.sl_usd,
              normalized_pnl
            });
          } else {
            // Fallback to fixed values if no child settings found
            normalized_pnl = isWin ? 20 : -20;
            console.warn(`Using fallback P&L calculation (±$20) for bot ${childBotId}. Missing:`, {
              hasChildSettings: !!childSettings,
              hasAmount: !!event.amount,
              validQuantity: childSettings?.quantity > 0
            });
          }

          return {
            timestamp: event.timestamp,
            instrument: normalizedInstrument,
            amount: event.amount || 0,
            action: event.action === 'EXIT_LONG' ? 'LONG' : 'SHORT',
            normalized_pnl
          };
        })
        .filter(item => item !== null) as MergedTradeHistoryItem[];

      console.log('Converted realtime items:', realtimeItems.length);
      return realtimeItems;
    } catch (error) {
      console.error('Error in fetchRealtimeExitEvents:', error);
      return [];
    }
  }


  /**
   * Get prop settings for P&L calculations (fallback only)
   */
  private static async getPropSettings(botId: string): Promise<{
    winPercentage: number;
    lossPercentage: number;
    minCapital: number;
  }> {
    try {
      // Find the original bot (shadow bots should use original bot's prop details)
      const linkedInfo = await findLinkedBotInfo(botId);
      const originalBotId = linkedInfo.currentBotType === 'shadow' ? linkedInfo.linkedBotId : botId;
      const queryBotId = originalBotId || botId;

      const { data: bot } = await supabase
        .from('bots')
        .select(`
          propDetails:bot_prop_details(
            win_percentage,
            loss_percentage,
            min_capital
          )
        `)
        .eq('id', queryBotId)
        .eq('type', 'prop_bot')
        .single();

      if (bot?.propDetails?.[0]) {
        const details = bot.propDetails[0];
        return {
          winPercentage: details.win_percentage ?? 2.0,
          lossPercentage: details.loss_percentage ?? 1.5,
          minCapital: Number(details.min_capital) ?? 100000
        };
      }
    } catch (error) {
      console.error('Error fetching prop settings:', error);
    }

    // Fallback to default values
    return {
      winPercentage: 2.0,
      lossPercentage: 1.5,
      minCapital: 100000
    };
  }

  static async getMergedTradingHistory(botId: string): Promise<MergedTradeHistoryItem[]> {
    const result = await this.getMergedTradingHistoryWithSource(botId);
    return result.trades;
  }

  static async getMergedTradingHistoryWithSource(botId: string): Promise<TradingHistoryWithSource> {
    try {
      console.log('getMergedTradingHistoryWithSource called with botId:', botId);
      
      // ⭐ Detect linked bot FIRST before any queries
      const linkedInfo = await findLinkedBotInfo(botId);
      const queryBotId = linkedInfo.linkedBotId || botId; // Use Shadow bot if available
      
      console.log('🔗 Linked bot detection:', {
        requestedBotId: botId,
        linkedBotId: linkedInfo.linkedBotId,
        currentBotType: linkedInfo.currentBotType,
        willQueryBotId: queryBotId
      });
      
      // PRIORITY 1: Load config from bot_merge_mapping (NEW - Direct config without analysis)
      const { data: mappingConfigs } = await supabase
        .from('bot_merge_mapping')
        .select('child_bot_id, instrument, tp_usd, sl_usd, quantity')
        .eq('parent_bot_id', queryBotId)
        .eq('is_active', true)
        .not('instrument', 'is', null);
      
      const mappingConfigMap: Record<string, any> = {};
      const mappingInstrumentMap = new Map<string, { childBotId: string }>();
      const mappingSettingsMap = new Map<string, { tp_usd: number; sl_usd: number; quantity: number }>();
      
      mappingConfigs?.forEach(m => {
        if (m.instrument && m.tp_usd && m.sl_usd && m.quantity) {
          mappingConfigMap[m.child_bot_id] = {
            instrument: m.instrument,
            tp_usd: m.tp_usd,
            sl_usd: m.sl_usd,
            quantity: m.quantity
          };
          
          const normalized = normalizeInstrument(m.instrument);
          mappingInstrumentMap.set(normalized, { 
            childBotId: m.child_bot_id 
          });
          mappingSettingsMap.set(m.child_bot_id, {
            tp_usd: m.tp_usd,
            sl_usd: m.sl_usd,
            quantity: m.quantity
          });
        }
      });
      
      console.log('Loaded mapping configs:', {
        count: Object.keys(mappingConfigMap).length,
        instruments: Array.from(mappingInstrumentMap.keys())
      });
      
      // PRIORITY 1B: Load config from bot_merge_analysis_settings (Historical Analysis Tab)
      const { data: analysisSettings } = await supabase
        .from('bot_merge_analysis_settings')
        .select('child_bot_id, instrument, tp_usd, sl_usd, quantity')
        .eq('parent_bot_id', queryBotId)
        .not('instrument', 'is', null);
      
      const analysisConfigMap: Record<string, any> = {};
      const analysisInstrumentMap = new Map<string, { childBotId: string }>();
      const analysisSettingsMap = new Map<string, { tp_usd: number; sl_usd: number; quantity: number }>();
      
      analysisSettings?.forEach(s => {
        if (s.instrument && s.tp_usd && s.sl_usd && s.quantity) {
          analysisConfigMap[s.child_bot_id] = {
            instrument: s.instrument,
            tp_usd: s.tp_usd,
            sl_usd: s.sl_usd,
            quantity: s.quantity
          };
          
          const normalized = normalizeInstrument(s.instrument);
          analysisInstrumentMap.set(normalized, { 
            childBotId: s.child_bot_id 
          });
          analysisSettingsMap.set(s.child_bot_id, {
            tp_usd: s.tp_usd,
            sl_usd: s.sl_usd,
            quantity: s.quantity
          });
        }
      });
      
      console.log('Loaded analysis settings configs:', {
        count: Object.keys(analysisConfigMap).length,
        instruments: Array.from(analysisInstrumentMap.keys())
      });
      
      // Merge configs: PRIORITY ORDER
      // 1. bot_merge_mapping (highest priority - Manual Bot Mapping tab)
      // 2. bot_merge_analysis_settings (middle priority - Historical Analysis tab upload)
      // 3. published analysis config (lowest priority - will be merged later)
      const mergedInstrumentMap = new Map([...analysisInstrumentMap, ...mappingInstrumentMap]);
      const mergedSettingsMap = new Map([...analysisSettingsMap, ...mappingSettingsMap]);
      
      console.log('Merged config sources:', {
        fromAnalysisSettings: analysisSettings?.length || 0,
        fromMapping: mappingConfigs?.length || 0,
        totalInstruments: mergedInstrumentMap.size,
        totalChildBots: mergedSettingsMap.size,
        allInstruments: Array.from(mergedInstrumentMap.keys())
      });
      
      // PRIORITY 2: Try current bot published analysis (FALLBACK)
      let mergedRun = await MergeAnalysisService.getLatestPublishedMergeAnalysis(queryBotId);
      let sourceBotId = queryBotId;
      let sourceBotName = linkedInfo.linkedBotId ? 'Linked Shadow Bot' : 'Current Bot';
      let sourceBotType: 'original' | 'shadow' = linkedInfo.currentBotType === 'original' && linkedInfo.linkedBotId ? 'shadow' : 'original';
      let isFromLinkedBot = !!linkedInfo.linkedBotId;
      
      console.log('Direct lookup result:', !!mergedRun, 'from bot:', sourceBotId);
      
      // Priority 3: Check bot_merge_mapping for current bot as child
      if (!mergedRun) {
        try {
          const { data: mapping } = await supabase
            .from('bot_merge_mapping')
            .select(`
              parent_bot_id,
              parent_bot:bots!parent_bot_id(name, type)
            `)
            .eq('child_bot_id', botId)
            .eq('is_active', true)
            .single();
          
          if (mapping?.parent_bot_id) {
            mergedRun = await MergeAnalysisService.getLatestPublishedMergeAnalysis(mapping.parent_bot_id);
            if (mergedRun) {
              sourceBotId = mapping.parent_bot_id;
              sourceBotName = mapping.parent_bot?.name || 'Parent Bot';
              sourceBotType = mapping.parent_bot?.type === 'shadow_bot' ? 'shadow' : 'original';
              console.log('Found run from parent mapping for current bot');
            }
          }
        } catch (error) {
          console.log('No merge mapping found for current bot');
        }
      }
      
      // Priority 4: Check bot_merge_mapping for linked bot as child (only if queryBotId != botId)
      if (!mergedRun && queryBotId !== botId) {
        try {
          const { data: mapping } = await supabase
            .from('bot_merge_mapping')
            .select(`
              parent_bot_id,
              parent_bot:bots!parent_bot_id(name, type)
            `)
            .eq('child_bot_id', botId)
            .eq('is_active', true)
            .single();
          
          if (mapping?.parent_bot_id) {
            mergedRun = await MergeAnalysisService.getLatestPublishedMergeAnalysis(mapping.parent_bot_id);
            if (mergedRun) {
              sourceBotId = mapping.parent_bot_id;
              sourceBotName = mapping.parent_bot?.name || 'Parent Bot';
              sourceBotType = mapping.parent_bot?.type === 'shadow_bot' ? 'shadow' : 'original';
              console.log('Found run from parent mapping for original bot');
            }
          }
        } catch (error) {
          console.log('No merge mapping found for original bot');
        }
      }
      
      if (!mergedRun || !mergedRun.stats?.detailed_trades) {
        console.log('No merge analysis run found or no detailed trades available');
        return {
          trades: [],
          source: {
            parentBotId: sourceBotId,
            parentBotName: sourceBotName,
            parentBotType: sourceBotType,
            isFromLinkedBot
          }
        };
      }
      
      console.log('Found run with', mergedRun.stats.detailed_trades.length, 'detailed trades from bot:', sourceBotId);
      
      // Get base trades from historical analysis
      const baseTrades = mergedRun.stats.detailed_trades as MergedTradeHistoryItem[];
      
      // Get realtime trades - merge mapping config + published analysis config
      let realtimeTrades: MergedTradeHistoryItem[] = [];
      
      // Merge configs: Use merged analysis + mapping configs, then override with published config
      const publishedInstrumentMap = mergedRun.config ? createInstrumentMapping(mergedRun.config) : new Map();
      const publishedSettingsMap = mergedRun.config ? createChildSettingsMap(mergedRun.config) : new Map();
      
      // Final merge: Published (lowest) -> Analysis Settings (middle) -> Mapping (highest)
      const finalInstrumentMap = new Map([...publishedInstrumentMap, ...mergedInstrumentMap]);
      const finalSettingsMap = new Map([...publishedSettingsMap, ...mergedSettingsMap]);
      
      console.log('Final config maps with all 3 sources:', {
        fromPublished: publishedSettingsMap.size,
        fromAnalysis: analysisSettings?.length || 0,
        fromMapping: mappingConfigs?.length || 0,
        finalInstruments: Array.from(finalInstrumentMap.keys()),
        finalChildBots: Array.from(finalSettingsMap.keys())
      });
      
      // Get child bot IDs from all sources
      const publishedChildBotIds = mergedRun.config ? getChildBotIds(mergedRun.config, mergedRun.child_bot_ids) : [];
      const analysisChildBotIds = Object.keys(analysisConfigMap);
      const mappingChildBotIds = Object.keys(mappingConfigMap);
      const allChildBotIds = [...new Set([...publishedChildBotIds, ...analysisChildBotIds, ...mappingChildBotIds])];
      
      if (allChildBotIds.length > 0 || finalInstrumentMap.size > 0) {
        try {
          
          // Create allowed bot IDs list (child bots + queryBotId + original botId if different)
          const allowedBotIds = [...allChildBotIds];
          if (!allowedBotIds.includes(queryBotId)) {
            allowedBotIds.push(queryBotId);
          }
          if (queryBotId !== botId && !allowedBotIds.includes(botId)) {
            allowedBotIds.push(botId);
          }
          
          if (allowedBotIds.length > 0) {
            console.log('Fetching realtime trades for allowed bots:', allowedBotIds);
            realtimeTrades = await this.fetchRealtimeExitEvents(allowedBotIds, finalInstrumentMap, finalSettingsMap);
          }
        } catch (error) {
          console.error('Error fetching realtime trades:', error);
        }
      }
      
      // Merge base trades with realtime trades, removing duplicates
      const allTrades = [...baseTrades];
      
      // Add realtime trades that don't duplicate existing ones
      realtimeTrades.forEach(realtimeTrade => {
        const isDuplicate = baseTrades.some(baseTrade => 
          baseTrade.timestamp === realtimeTrade.timestamp &&
          baseTrade.instrument === realtimeTrade.instrument &&
          baseTrade.action === realtimeTrade.action &&
          Math.abs(baseTrade.amount - realtimeTrade.amount) < 0.01
        );
        
        if (!isDuplicate) {
          allTrades.push(realtimeTrade);
        }
      });
      
      // Sort by timestamp descending (newest first)
      allTrades.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
      
      console.log('Total trades (base + realtime):', allTrades.length, '(base:', baseTrades.length, ', realtime:', realtimeTrades.length, ')');
      
      return {
        trades: allTrades,
        source: {
          parentBotId: sourceBotId,
          parentBotName: sourceBotName,
          parentBotType: sourceBotType,
          isFromLinkedBot
        }
      };
    } catch (error) {
      console.error('Error in getMergedTradingHistoryWithSource:', error);
      return {
        trades: [],
        source: null
      };
    }
  }
}